/**
 * src/marketing/sections.tsx - composable, content-driven blocks for the marketing site.
 *
 * These are presentation-only and read from src/marketing/content.ts. Pages assemble them.
 * All color/type come from src/theme tokens (no raw hex/fonts); marketing-specific layout
 * numbers (widths, grid gaps) live here with the marketing code.
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, View, useWindowDimensions, type DimensionValue, type LayoutChangeEvent } from 'react-native';
import { Text, Card } from '../components/ui';
import { BeamMark } from '../components/brand/Logo';
import { theme } from '../theme';
import { MAX_W, useMarketingNav } from './shell';
import { HERO, TRUST, FEATURES, STEPS, PRICING, PRICING_FAQ, type BillingInterval } from './content';

// --- Layout primitives ---------------------------------------------------------------------

export function Container({ children, style }: { children: React.ReactNode; style?: object }) {
  return <View style={[layout.container, { maxWidth: MAX_W }, style]}>{children}</View>;
}

/** A full-width band with vertical rhythm. `id` registers it as an in-page scroll anchor. */
export function Section({
  children,
  id,
  tone = 'paper',
}: {
  children: React.ReactNode;
  id?: string;
  tone?: 'paper' | 'surface' | 'ink';
}) {
  const { register } = useMarketingNav();
  const bg =
    tone === 'ink' ? theme.palette.indigo : tone === 'surface' ? theme.color.bg.surface : theme.color.bg.app;
  const onLayout = id ? (e: LayoutChangeEvent) => register(id, e.nativeEvent.layout.y) : undefined;
  return (
    <View onLayout={onLayout} style={[layout.section, { backgroundColor: bg }]}>
      <Container>{children}</Container>
    </View>
  );
}

export function Eyebrow({ children, onInk }: { children: string; onInk?: boolean }) {
  return (
    <Text variant="label" style={onInk ? { color: theme.palette.copper } : { color: theme.color.text.link }}>
      {children}
    </Text>
  );
}

// --- Hero ----------------------------------------------------------------------------------

export function Hero({ onCta }: { onCta: (href: string) => void }) {
  return (
    <View style={layout.heroWrap}>
      {/* faint aperture motif, top-right */}
      <View style={layout.heroMotif} pointerEvents="none">
        <BeamMark size={460} tone="light" />
      </View>
      <Container style={layout.heroInner}>
        <Eyebrow onInk>{HERO.eyebrow}</Eyebrow>
        <Text variant="display" style={[layout.heroTitle, { color: theme.palette.paper }]}>
          {HERO.title}
        </Text>
        <Text variant="subhead" style={[layout.heroSub, { color: theme.palette.indigoWash }]}>
          {HERO.subtitle}
        </Text>
        <View style={layout.heroCtas}>
          <HeroButton label={HERO.primaryCta.label} onPress={() => onCta(HERO.primaryCta.href)} variant="accent" />
          <HeroButton label={HERO.secondaryCta.label} onPress={() => onCta(HERO.secondaryCta.href)} variant="ghostLight" />
        </View>
        <Text variant="small" style={{ color: theme.palette.indigoWash }}>
          {HERO.note}
        </Text>
        <View style={layout.trustRow}>
          {TRUST.map((t) => (
            <View key={t} style={layout.trustItem}>
              <Dot />
              <Text variant="small" style={{ color: theme.palette.paper }}>
                {t}
              </Text>
            </View>
          ))}
        </View>
      </Container>
    </View>
  );
}

function Dot() {
  return <View style={layout.dot} />;
}

/** Hero CTA - a marketing button (filled copper or light-outline) drawn on the ink hero. */
function HeroButton({
  label,
  onPress,
  variant,
}: {
  label: string;
  onPress: () => void;
  variant: 'accent' | 'ghostLight';
}) {
  const accent = variant === 'accent';
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        layout.heroBtn,
        accent
          ? { backgroundColor: pressed ? theme.palette.copperWash : theme.palette.copper }
          : { borderWidth: theme.layout.hairline, borderColor: theme.palette.indigoWash, backgroundColor: pressed ? theme.palette.blueprint : 'transparent' },
      ]}
    >
      <Text variant="subhead" style={{ color: theme.palette.paper }}>
        {label}
      </Text>
    </Pressable>
  );
}

// --- Feature grid --------------------------------------------------------------------------

export function FeatureGrid() {
  const { width } = useWindowDimensions();
  const cols = width >= 900 ? 3 : width >= 560 ? 2 : 1;
  const basis = `${100 / cols}%`;
  return (
    <>
      <Eyebrow>Everything in one place</Eyebrow>
      <Text variant="title" style={layout.sectionTitle}>
        One app for the whole job
      </Text>
      <View style={layout.grid}>
        {FEATURES.map((f) => (
          <View key={f.title} style={[layout.gridCell, { flexBasis: basis as DimensionValue, maxWidth: basis as DimensionValue }]}>
            <Card style={layout.featureCard}>
              <View style={layout.featureHead}>
                <BeamMark size={22} />
                <Text variant="subhead" style={layout.flex1}>
                  {f.title}
                </Text>
              </View>
              <Text variant="body" color="secondary" style={layout.featureBody}>
                {f.body}
              </Text>
            </Card>
          </View>
        ))}
      </View>
    </>
  );
}

// --- How it works --------------------------------------------------------------------------

export function Steps() {
  const { width } = useWindowDimensions();
  const cols = width >= 900 ? 4 : width >= 560 ? 2 : 1;
  const basis = `${100 / cols}%`;
  return (
    <>
      <Eyebrow>How it works</Eyebrow>
      <Text variant="title" style={layout.sectionTitle}>
        From first call to final payment
      </Text>
      <View style={layout.grid}>
        {STEPS.map((s) => (
          <View key={s.step} style={[layout.gridCell, { flexBasis: basis as DimensionValue, maxWidth: basis as DimensionValue }]}>
            <View style={layout.step}>
              <Text variant="amount" style={{ color: theme.palette.copper }}>
                {s.step}
              </Text>
              <Text variant="subhead">{s.title}</Text>
              <Text variant="body" color="secondary">
                {s.body}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </>
  );
}

// --- CTA band ------------------------------------------------------------------------------

export function CtaBand({ onCta }: { onCta: (href: string) => void }) {
  return (
    <View style={layout.ctaBand}>
      <Text variant="title" style={[layout.ctaTitle, { color: theme.palette.paper }]}>
        Put your whole operation on Beam.
      </Text>
      <Text variant="subhead" style={[layout.ctaSub, { color: theme.palette.indigoWash }]}>
        $49 a month for up to 5 users. Contact us for unlimited.
      </Text>
      <View style={layout.heroCtas}>
        <HeroButton label="Get started" onPress={() => onCta('/pricing')} variant="accent" />
        <HeroButton label="Download the app" onPress={() => onCta('/download')} variant="ghostLight" />
      </View>
    </View>
  );
}

// --- Pricing cards -------------------------------------------------------------------------

export function PricingCards({
  onSelect,
}: {
  onSelect: (planId: string, selfServe: boolean, interval: BillingInterval) => void;
}) {
  const { width } = useWindowDimensions();
  const cols = width >= 760 ? 2 : 1;
  const basis = `${100 / cols}%`;
  const [interval, setInterval] = useState<BillingInterval>('month');
  return (
    <>
      <View style={layout.billingToggle} accessibilityRole="radiogroup">
        {PRICING.intervals.map((opt) => {
          const active = interval === opt.id;
          return (
            <Pressable
              key={opt.id}
              accessibilityRole="radio"
              accessibilityState={{ selected: active }}
              onPress={() => setInterval(opt.id)}
              style={[layout.billingOption, active ? layout.billingOptionActive : null]}
            >
              <Text variant="bodyStrong" color={active ? 'primary' : 'secondary'}>
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      <View style={layout.grid}>
        {PRICING.plans.map((p) => {
          const annual = interval === 'year' ? p.annual : null;
          const priceDisplay = annual ? annual.priceDisplay : p.priceDisplay;
          const unit = annual ? annual.unit : p.unit;
          const note = annual ? annual.note : null;
          return (
        <View key={p.id} style={[layout.gridCell, { flexBasis: basis as DimensionValue, maxWidth: basis as DimensionValue }]}>
          <Card style={[layout.priceCard, p.featured ? layout.priceCardFeatured : null]}>
            {p.featured ? (
              <View style={layout.badge}>
                <Text variant="label" style={{ color: theme.palette.paper }}>
                  Most popular
                </Text>
              </View>
            ) : null}
            <Text variant="subhead">{p.name}</Text>
            <Text variant="small" color="secondary">
              {p.tagline}
            </Text>
            <View style={layout.priceRow}>
              <Text variant="display">{priceDisplay}</Text>
              {unit ? (
                <Text variant="body" color="secondary">
                  {unit}
                </Text>
              ) : null}
            </View>
            {note ? (
              <Text variant="small" color="secondary" style={layout.priceNote}>
                {note}
              </Text>
            ) : null}
            <View style={layout.featureList}>
              {p.features.map((f) => (
                <View key={f} style={layout.featureLine}>
                  <Text variant="bodyStrong" style={{ color: theme.palette.copper }}>
                    ✓
                  </Text>
                  <Text variant="body" color="secondary" style={layout.flex1}>
                    {f}
                  </Text>
                </View>
              ))}
            </View>
            <Pressable
              accessibilityRole="button"
              onPress={() => onSelect(p.id, p.selfServe, interval)}
              style={({ pressed }) => [
                layout.priceCta,
                p.featured
                  ? { backgroundColor: pressed ? theme.palette.blueprint : theme.color.action.primary }
                  : { borderWidth: theme.layout.hairline, borderColor: theme.color.border.strong, backgroundColor: pressed ? theme.color.bg.subtle : 'transparent' },
              ]}
            >
              <Text variant="bodyStrong" color={p.featured ? 'onPrimary' : 'primary'}>
                {p.cta}
              </Text>
            </Pressable>
          </Card>
        </View>
          );
        })}
      </View>
    </>
  );
}

// --- FAQ -----------------------------------------------------------------------------------

export function FaqList() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <>
      <Eyebrow>FAQ</Eyebrow>
      <Text variant="title" style={layout.sectionTitle}>
        Questions, answered
      </Text>
      <View style={layout.faq}>
        {PRICING_FAQ.map((item, i) => {
          const isOpen = open === i;
          return (
            <Pressable key={item.q} accessibilityRole="button" onPress={() => setOpen(isOpen ? null : i)} style={layout.faqItem}>
              <View style={layout.faqQ}>
                <Text variant="bodyStrong" style={layout.flex1}>
                  {item.q}
                </Text>
                <Text variant="subhead" color="secondary">
                  {isOpen ? '–' : '+'}
                </Text>
              </View>
              {isOpen ? (
                <Text variant="body" color="secondary" style={layout.faqA}>
                  {item.a}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
      </View>
    </>
  );
}

const layout = StyleSheet.create({
  container: { width: '100%', alignSelf: 'center', paddingHorizontal: theme.spacing.lg },
  section: { paddingVertical: theme.spacing['4xl'], paddingHorizontal: theme.spacing.lg },
  sectionTitle: { marginTop: theme.spacing.sm, marginBottom: theme.spacing.xl },

  heroWrap: { backgroundColor: theme.palette.indigo, overflow: 'hidden', paddingHorizontal: theme.spacing.lg },
  heroMotif: { position: 'absolute', right: -120, top: -80, opacity: 0.07 },
  heroInner: { paddingVertical: theme.spacing['4xl'] * 1.6, gap: theme.spacing.lg, maxWidth: 820 },
  heroTitle: { marginTop: theme.spacing.sm },
  heroSub: { maxWidth: 640, lineHeight: 26 },
  heroCtas: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.md, marginTop: theme.spacing.sm },
  heroBtn: { height: 52, paddingHorizontal: theme.spacing['2xl'], borderRadius: theme.radius.md, alignItems: 'center', justifyContent: 'center' },
  trustRow: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.xl, marginTop: theme.spacing.xl },
  trustItem: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  dot: { width: 6, height: 6, borderRadius: theme.radius.pill, backgroundColor: theme.palette.copper },

  grid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -theme.spacing.md / 2 },
  gridCell: { paddingHorizontal: theme.spacing.md / 2, paddingVertical: theme.spacing.md / 2 },
  featureCard: { height: '100%', gap: theme.spacing.sm },
  // Logo sits inline next to the heading (no background); the body aligns under the
  // heading text via a left inset equal to the logo width + the row gap.
  featureHead: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  featureBody: { paddingLeft: 22 + theme.spacing.sm },
  step: { gap: theme.spacing.xs, paddingRight: theme.spacing.md },

  ctaBand: { backgroundColor: theme.palette.indigo, paddingVertical: theme.spacing['4xl'], paddingHorizontal: theme.spacing.lg, alignItems: 'center', gap: theme.spacing.md },
  ctaTitle: { textAlign: 'center' },
  ctaSub: { textAlign: 'center' },

  // Monthly/Annual segmented control above the pricing cards.
  billingToggle: {
    flexDirection: 'row', alignSelf: 'flex-start', gap: theme.spacing.xs,
    padding: theme.spacing.xs, marginBottom: theme.spacing.xl,
    backgroundColor: theme.color.bg.surface, borderRadius: theme.radius.pill,
    borderWidth: theme.layout.hairline, borderColor: theme.color.border.default,
  },
  billingOption: { paddingHorizontal: theme.spacing.lg, paddingVertical: theme.spacing.sm, borderRadius: theme.radius.pill },
  billingOptionActive: { backgroundColor: theme.color.bg.app },

  priceCard: { height: '100%', gap: theme.spacing.sm },
  priceCardFeatured: { borderColor: theme.color.action.primary, borderWidth: 2 },
  badge: { alignSelf: 'flex-start', backgroundColor: theme.color.action.primary, borderRadius: theme.radius.pill, paddingHorizontal: theme.spacing.md, paddingVertical: theme.spacing.xs },
  priceRow: { flexDirection: 'row', alignItems: 'flex-end', gap: theme.spacing.xs, marginVertical: theme.spacing.sm },
  priceNote: { marginTop: -theme.spacing.xs, marginBottom: theme.spacing.sm },
  featureList: { gap: theme.spacing.sm, marginBottom: theme.spacing.md },
  featureLine: { flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.sm },
  flex1: { flex: 1 },
  priceCta: { height: theme.layout.fieldHeight, borderRadius: theme.radius.md, alignItems: 'center', justifyContent: 'center', marginTop: 'auto' },

  faq: { gap: theme.spacing.sm },
  faqItem: { borderWidth: theme.layout.hairline, borderColor: theme.color.border.default, borderRadius: theme.radius.md, padding: theme.spacing.lg, backgroundColor: theme.color.bg.surface },
  faqQ: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
  faqA: { marginTop: theme.spacing.sm, lineHeight: 23 },
});
